import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Spinner, Modal } from 'react-bootstrap';
import { BsPlusCircleFill, BsPencilSquare, BsTrash, BsCheckLg, BsX } from 'react-icons/bs';
import { FaList } from 'react-icons/fa';
import axios from 'axios';
import { toast } from 'react-toastify';
import DepartmentSidebar from './components/DepertmentSidebar';
import DepartmentTopbar from './components/DepartmentTopbar';

function CashCategory() {
  const [categoryName, setCategoryName] = useState('');
  const [categories, setCategories] = useState([]);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingCategoryName, setEditingCategoryName] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);

  const API_URL = process.env.REACT_APP_SUPER_ADMIN_API_URL;
  const token = localStorage.getItem('token');

  const fetchCategories = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}cash_catagory_list.php`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        const formattedCategories = response.data.data.map((cat) => ({
          id: cat.id,
          name: cat.catagory_name,
        }));
        setCategories(formattedCategories);
      } else {
        toast.error(`Error fetching categories: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while fetching categories.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  const handleCreateCategory = async (event) => {
    event.preventDefault();
    const trimmedName = categoryName.trim();
    if (!trimmedName) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}add_cash_catagory.php`, {
        catagory_name: trimmedName,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        toast.success(response.data.message);
        setCategoryName('');
        fetchCategories(); // Refetch to update the list
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while adding the category.');
    } finally {
      setLoading(false);
    }
  };

  const startEditCategory = (category) => {
    setEditingCategoryId(category.id);
    setEditingCategoryName(category.name);
  };

  const cancelEditCategory = () => {
    setEditingCategoryId(null);
    setEditingCategoryName('');
  };

  const handleUpdateCategory = async () => {
    const trimmedName = editingCategoryName.trim();
    if (!trimmedName) {
      return;
    }

    try {
      setLoading(true);
      const response = await axios.post(`${API_URL}update_cash_catagory.php`, {
        id: editingCategoryId,
        catagory_name: trimmedName,
      }, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.data.success) {
        toast.success('Category updated successfully.');
        fetchCategories(); // Refetch to update the list
      } else {
        toast.error(`Error: ${response.data.message}`);
      }
    } catch (error) {
      toast.error('An error occurred while updating the category.');
    } finally {
      setLoading(false);
    }

    cancelEditCategory();
  };

  const handleDeleteClick = (categoryId) => {
    setCategoryToDelete(categoryId);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!categoryToDelete) return;
    
    try {
      setLoading(true);
      const response = await axios.post(
        `${API_URL}delete_cash_catagory.php`, 
        { id: categoryToDelete }, 
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.success) {
        toast.success('Category deleted successfully.');
        fetchCategories();
      } else {
        const errorMessage = response.data.message || 'Failed to delete category';
        toast.error(errorMessage);
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'An error occurred while deleting the category.';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setCategoryToDelete(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setCategoryToDelete(null);
  };

  return (
    <>
      <DepartmentTopbar />
      <div className="d-flex">
        <DepartmentSidebar />
        <Container fluid className="p-4 bg-light">
          <header className="mb-4">
            <h1 className="h3">Cash Management</h1>
          </header>
          <Row className="g-4">
            <Col lg={4}>
              <Card className="shadow-sm h-100">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center gap-2"
                  style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}
                >
                  <BsPlusCircleFill style={{ color: '#1976d2' }} />
                  <span style={{ color: '#1976d2' }}>Create New Cash Category</span>
                </Card.Header>
                <Card.Body>
                  <Form onSubmit={handleCreateCategory}>
                    <Form.Group controlId="cashCategoryName" className="mb-3">
                      <Form.Label>Category Name</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Enter New Category name"
                        value={categoryName}
                        onChange={(event) => setCategoryName(event.target.value)}
                      />
                    </Form.Group>
                    <div className="d-grid">
                      <Button type="submit" variant="success" disabled={loading}>
                        {loading ? <Spinner animation="border" size="sm" /> : 'Create Category'}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Col>
            <Col lg={8}>
              <Card className="shadow-sm h-100">
                <Card.Header
                  as="h5"
                  className="d-flex align-items-center gap-2"
                  style={{ backgroundColor: '#e3f2fd', borderBottom: '2px solid #1976d2' }}
                >
                  <FaList style={{ color: '#1976d2' }} />
                  <span style={{ color: '#1976d2' }}>Category List</span>
                </Card.Header>
                <Card.Body>
                  <div className="table-responsive">
                    <Table bordered hover responsive="sm">
                      <thead className="table-dark" style={{ backgroundColor: '#0d6efd' }}>
                        <tr>
                          <th>Category Name</th>
                          <th style={{ width: '160px' }}>Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {categories.length === 0 ? (
                          <tr>
                            <td colSpan={2} className="text-center text-muted">
                              No categories available. Create a new category to get started.
                            </td>
                          </tr>
                        ) : (
                          categories.map((category) => (
                            <tr key={category.id}>
                              <td>
                                {editingCategoryId === category.id ? (
                                  <Form.Control
                                    size="sm"
                                    value={editingCategoryName}
                                    onChange={(event) => setEditingCategoryName(event.target.value)}
                                  />
                                ) : (
                                  category.name
                                )}
                              </td>
                              <td>
                                <div className="d-flex gap-2">
                                  {editingCategoryId === category.id ? (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="success"
                                        onClick={handleUpdateCategory}
                                        disabled={loading}
                                      >
                                        <BsCheckLg />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="secondary"
                                        onClick={cancelEditCategory}
                                      >
                                        <BsX />
                                      </Button>
                                    </>
                                  ) : (
                                    <>
                                      <Button
                                        size="sm"
                                        variant="outline-primary"
                                        onClick={() => startEditCategory(category)}
                                      >
                                        <BsPencilSquare />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline-danger"
                                        onClick={() => handleDeleteClick(category.id)}
                                        disabled={loading}
                                      >
                                        <BsTrash />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete this category? This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal} disabled={loading}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmDelete} disabled={loading}>
            {loading ? 'Deleting...' : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default CashCategory;
